import {
  ReactNode, ComponentType, KeyboardEvent, SyntheticEvent,
} from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, {
  AutocompleteRenderInputParams,
  AutocompleteRenderGetTagProps,
} from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';

import {
  KEY_CODES,
} from '#utils/constants';

interface AutocompleteOption {
  id: number;
  name: string;
}

interface AutocompleteChange {
  name: string;
  value: Array<number | string>;
}

interface EnhancedAutocompleteProps {
  error?: boolean;
  helperText?: ReactNode;
  label?: ReactNode;
  lockedIds?: number[];
  selectedIdsName?: string;
  selectedIds?: number[];
  newValuesName?: string;
  newValues?: string[];
  onChange?: (event: unknown, changes: AutocompleteChange[]) => void;
  minSize?: number;
  options?: AutocompleteOption[];
  getOptionLabel?: (option: AutocompleteOption) => string;
  getOptionId?: (option: AutocompleteOption) => number;
  value?: unknown;
  [key: string]: unknown;
}

// The wrapper munges a dynamic set of MUI Autocomplete props; type the wrapper's
// own logic strictly and relax only the final passthrough to Autocomplete itself.
const LooseAutocomplete = Autocomplete as unknown as ComponentType<Record<string, unknown>>;

// extends for onAdd event
export default function EnhancedAutocomplete(props: EnhancedAutocompleteProps) {
  const {
    error,
    helperText,
    label,
    lockedIds,
    selectedIdsName,
    selectedIds,
    newValuesName,
    newValues,
    onChange,
    minSize,
    ...rest
  } = props;

  if (rest.value) {
    throw new Error('value is not available for enhanced autocomplete, use selectedIds instead.');
  }

  if (!onChange) {
    throw new Error('onChange is required for enhanced autocomplete.');
  }

  if (selectedIdsName ? !selectedIds : selectedIds) {
    throw new Error('selectedIds and selectedIdsName should both exists');
  }

  if (newValuesName ? !newValues : newValues) {
    throw new Error('newValues and newValuesName should both exists');
  }

  const {
    options: _options,
    getOptionLabel: _getOptionLabel,
    getOptionId: _getOptionId,
    value: _value,
    ...passthrough
  } = rest;

  const getOptionLabel = (rest.getOptionLabel as ((option: AutocompleteOption) => string))
    || ((option: AutocompleteOption) => option.name || '');
  const getOptionId = (rest.getOptionId as ((option: AutocompleteOption) => number))
    || ((option: AutocompleteOption) => option.id || -1);

  const options: AutocompleteOption[] = [...((rest.options as AutocompleteOption[]) || [])];
  const value: AutocompleteOption[] = options.filter((o) => (selectedIds || []).includes(o.id));

  let noOptionsText: ReactNode;
  let onKeyUp: ((event: KeyboardEvent<HTMLDivElement>) => void) | undefined;

  if (newValues) {
    const optionNames = options.map(getOptionLabel);
    noOptionsText = 'Press "Enter" to add new collection';

    newValues.forEach((nv, idx) => {
      const val: AutocompleteOption = {
        id: -1 - idx,
        name: nv,
      };

      options.push(val);
      value.push(val);
    });

    onKeyUp = (event) => {
      if (event.keyCode !== KEY_CODES.ENTER) {
        return;
      }

      const newValue = (event.target as HTMLInputElement).value.trim();
      if (!newValue) {
        return;
      }

      if (optionNames.includes(newValue)) {
        return;
      }

      onChange(null, [{
        name: newValuesName!,
        value: [...newValues, newValue],
      }]);
    };
  }

  const handleChange = (event: SyntheticEvent, newValue: AutocompleteOption[], reason: string) => {
    if (reason === 'removeOption'
      && (event as unknown as KeyboardEvent).keyCode === KEY_CODES.BACKSPACE) {
      return;
    }

    const newSelectedIds = newValue.filter((nv) => nv.id >= 0).map((nv) => nv.id);
    const newNewValues = newValue.filter((nv) => nv.id < 0).map((nv) => nv.name);

    const changeValues: AutocompleteChange[] = [{
      name: selectedIdsName!,
      value: newSelectedIds,
    }];

    if (newValuesName) {
      changeValues.push({
        name: newValuesName,
        value: newNewValues,
      });
    }

    onChange(null, changeValues);
  };

  return (
    <LooseAutocomplete
      {...passthrough}
      options={options}
      value={value}
      getOptionLabel={getOptionLabel}
      noOptionsText={noOptionsText}
      onKeyUp={onKeyUp}
      onChange={handleChange}
      disableClearable
      renderInput={(params: AutocompleteRenderInputParams) => {
        return (
          <TextField
            {...params}
            label={label}
            error={error}
            helperText={helperText}
          />
        );
      }}
      renderValue={(tagValue: AutocompleteOption[], getItemProps: AutocompleteRenderGetTagProps) => {
        const disabledBySize = (minSize && tagValue.length <= minSize);
        return tagValue.map((option, index) => {
          const { key, ...tagProps } = getItemProps({ index });
          return (
            <Chip
              key={key}
              label={getOptionLabel(option)}
              {...tagProps}
              disabled={!!disabledBySize || (!!lockedIds && lockedIds.includes(getOptionId(option)))}
            />
          );
        });
      }}
    />
  );
}
